import { TrendingUp, TrendingDown } from "lucide-react";
import { TV_BLUE } from "@/components/broadcast/TvFrame";
import { TvValueMap, TvWindMap } from "@/components/broadcast/TvMap";
import MapWeatherFx from "@/components/broadcast/MapWeatherFx";
import { iconFor, HATCH, BrightStage, Pill, CITY_COORDS, toPoints, tonightPeriod, tomorrowPeriod, precipPercent } from "@/components/broadcast/shared";

function FeatureCitySlide({ city }) {
  const hot = (city.current_temp ?? 70) >= 85, cold = (city.current_temp ?? 70) <= 40;
  const panel = hot ? "linear-gradient(160deg, #f26419 0%, #d90429 120%)" : cold ? "linear-gradient(160deg, #4cc9f0 0%, #1f63c9 120%)" : "linear-gradient(160deg, #1f63c9 0%, #0a2a66 130%)";
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="feature-city-slide" style={{ background: "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)" }}>
      <div className="absolute inset-y-0 right-0 w-[44%]" style={{ background: panel, clipPath: "polygon(16% 0, 100% 0, 100% 100%, 0 100%)" }} />
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />
      <div className="relative flex h-full w-full items-center justify-between px-[80px]">
        <div className="max-w-[980px]">
          <div className="inline-flex items-center gap-3 rounded-full px-5 py-2 font-oswald text-[20px] font-bold uppercase tracking-[0.3em] text-white" style={{ background: TV_BLUE }}>
            <span className="live-dot" style={{ width: 10, height: 10 }} /> Right now
          </div>
          <div className="mt-4 font-oswald text-[120px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]">{city.name}</div>
          <div className="mt-2 font-oswald text-[44px] font-bold uppercase tracking-wider" style={{ color: TV_BLUE }}>{city.condition}</div>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[["High", `${city.high ?? "—"}°`], ["Low", `${city.low ?? "—"}°`], ["Wind", city.wind || "—"],
              ["Feels Like", city.feels_like != null ? `${city.feels_like}°` : "—"], ["Humidity", city.humidity != null ? `${city.humidity}%` : "—"], ["Dewpoint", city.dewpoint != null ? `${city.dewpoint}°` : "—"]].map(([k, v]) => (
              <div key={k} className="rounded-[4px] bg-white px-6 py-4 shadow-[0_10px_30px_rgba(10,15,28,0.12)]">
                <div className="font-oswald text-[18px] font-bold uppercase tracking-[0.2em] text-[#5b6675]">{k}</div>
                <div className="mt-1 font-oswald text-[46px] font-black leading-none text-[#0a0f1c]">{v}</div>
              </div>
            ))}
          </div>
          {city.detailed_forecast && (
            <div className="mt-6 max-w-[900px] font-oswald text-[24px] leading-snug text-[#2c3542] line-clamp-2">{city.detailed_forecast}</div>
          )}
        </div>
        <div className="relative text-right text-white">
          <div className="mb-2 flex justify-end">{iconFor(city.condition, "h-[200px] w-[200px] drop-shadow-[0_12px_30px_rgba(0,0,0,0.35)]")}</div>
          <div className="font-oswald text-[300px] font-black leading-[0.85] drop-shadow-[0_12px_30px_rgba(0,0,0,0.25)]" data-testid="feature-city-temp">
            {city.current_temp != null ? city.current_temp : "—"}
            <span className="ml-2 align-top text-[80px] font-bold">°F</span>
          </div>
        </div>
      </div>
    </div>
  );
}
function ExtremesSlide({ cities }) {
  const valid = cities.filter((c) => typeof c.current_temp === "number");
  const hi = valid.reduce((a, b) => (a.current_temp >= b.current_temp ? a : b), valid[0]);
  const lo = valid.reduce((a, b) => (a.current_temp <= b.current_temp ? a : b), valid[0]);
  if (!hi || !lo) return null;
  const half = (c, label, grad, Icon, side) => (
    <div className={`relative flex flex-col justify-center px-[90px] text-white ${side === "left" ? "items-start" : "items-end text-right"}`} style={{ background: grad }} data-testid={`extremes-${label.toLowerCase()}`}>
      <Pill color="rgba(0,0,0,0.35)"><Icon className="h-5 w-5" /> {label} right now</Pill>
      <div className="mt-6 font-oswald text-[84px] font-black uppercase leading-none tracking-wide drop-shadow-[0_8px_24px_rgba(0,0,0,0.3)]">{c.name}</div>
      <div className="mt-2 font-oswald text-[280px] font-black leading-[0.85] drop-shadow-[0_12px_30px_rgba(0,0,0,0.3)]">{c.current_temp}<span className="ml-2 align-top text-[70px] font-bold">°F</span></div>
      <div className="mt-6 flex items-center gap-4 font-oswald text-[36px] font-bold uppercase tracking-wider text-white/90">{iconFor(c.condition, "h-14 w-14")} {c.condition}</div>
    </div>
  );
  return (
    <BrightStage testid="extremes-slide">
      <div className="relative grid h-full w-full grid-cols-2 overflow-hidden">
        {half(hi, "Hottest", "linear-gradient(160deg, #f26419 0%, #d90429 120%)", TrendingUp, "left")}
        {half(lo, "Coolest", "linear-gradient(200deg, #4cc9f0 0%, #1f63c9 120%)", TrendingDown, "right")}
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: HATCH }} />
        <div className="absolute left-1/2 top-1/2 flex h-[200px] w-[200px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-white shadow-[0_20px_60px_rgba(10,15,28,0.35)]" data-testid="extremes-spread">
          <div className="font-oswald text-[18px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">Spread</div>
          <div className="font-oswald text-[84px] font-black leading-none text-[#0a0f1c]">{hi.current_temp - lo.current_temp}°</div>
        </div>
      </div>
    </BrightStage>
  );
}
function BentoSlide({ cities }) {
  return (
    <BrightStage testid="bento-slide" className="p-[48px]">
      <div className="mb-5 flex items-center justify-between">
        <Pill><span className="live-dot" style={{ width: 10, height: 10 }} /> Current conditions</Pill>
        <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">National Weather Service observations</div>
      </div>
      <div className="grid h-[calc(100%-80px)] grid-cols-4 grid-rows-3 gap-4">
        {cities.slice(0, 12).map((c) => (
          <div key={c.id} className="flex flex-col justify-between rounded-[6px] bg-white p-5 shadow-[0_12px_30px_rgba(10,15,28,0.12)]" data-testid={`bento-${c.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-oswald text-[30px] font-black uppercase tracking-wide text-[#0a0f1c]">{c.name}</div>
              {iconFor(c.condition, "h-12 w-12")}
            </div>
            <div className="flex items-end justify-between gap-3">
              <div className="font-oswald text-[96px] font-black leading-[0.85]" style={{ color: TV_BLUE }}>
                {c.current_temp != null ? c.current_temp : "—"}<span className="ml-1 align-top text-[30px] font-bold text-[#5b6675]">°</span>
              </div>
              <div className="max-w-[55%] text-right font-oswald text-[22px] font-bold uppercase leading-tight tracking-wide text-[#5b6675]">{c.condition}</div>
            </div>
          </div>
        ))}
      </div>
    </BrightStage>
  );
}
function SevenDaySlide({ city }) {
  const periods = (city?.forecast_periods || []).slice(0, 7);
  return (
    <BrightStage testid="seven-day-slide" className="p-[48px]">
      <div className="mb-5 flex items-center justify-between">
        <Pill>{city?.name} · Extended forecast</Pill>
        <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">National Weather Service</div>
      </div>
      <div className="grid h-[calc(100%-80px)] grid-cols-7 gap-4">
        {periods.map((p, i) => {
          const t = typeof p.temperature === "number" ? p.temperature : null;
          const night = p.isDaytime === false || /night/i.test(p.name || "");
          return (
            <div key={i} className="flex flex-col overflow-hidden rounded-[6px] bg-white text-center shadow-[0_12px_30px_rgba(10,15,28,0.12)]" data-testid={`period-${i}`}>
              <div className="px-2 py-3 font-oswald text-[24px] font-black uppercase leading-tight tracking-wide text-white" style={{ background: night ? "#0a0f1c" : TV_BLUE }}>{p.name || "—"}</div>
              <div className="flex flex-1 flex-col items-center justify-center px-4">
                {iconFor(p.shortForecast, "h-[110px] w-[110px]")}
                <div className="mt-4 font-oswald text-[96px] font-black leading-none" style={{ color: night ? "#0a0f1c" : TV_BLUE }}>{t != null ? `${t}°` : "—"}</div>
                <div className="mt-1 font-oswald text-[16px] font-bold uppercase tracking-[0.25em] text-[#5b6675]">{night ? "Low" : "High"}</div>
                <div className="mt-4 line-clamp-3 font-oswald text-[24px] font-bold uppercase leading-snug tracking-wide text-[#2c3542]">{p.shortForecast || ""}</div>
              </div>
            </div>
          );
        })}
      </div>
    </BrightStage>
  );
}
function TempOutlookSlide({ cities, mode }) {
  // mode = "highs" | "lows"
  const isHighs = mode === "highs";
  const rows = cities
    .map((c) => {
      const p = isHighs ? tomorrowPeriod(c) : tonightPeriod(c);
      return {
        id: c.id,
        name: c.name,
        temp: typeof p?.temperature === "number" ? p.temperature : null,
        forecast: p?.shortForecast || "",
        precip: precipPercent(p),
        periodName: p?.name || "",
      };
    })
    .filter((r) => r.temp != null);

  if (!rows.length) return null;
  const points = rows.map((r) => ({ id: r.id, name: r.name, value: r.temp, ...(CITY_COORDS[r.id] || {}) })).filter((p) => p.lat != null);
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid={`tv-map-slide-${mode}`}>
      <TvValueMap points={points} />
    </div>
  );
}
function BannerMapSlide({ cities, valueKey, valueSuffix = "°" }) {
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid={`tv-map-slide-${valueKey}`}>
      <TvValueMap points={toPoints(cities, valueKey)} suffix={valueSuffix === "°" ? "" : valueSuffix} />
      <MapWeatherFx cities={cities} />
    </div>
  );
}
function GATempsSlide({ cities })       { return <BannerMapSlide cities={cities} valueKey="current_temp" />; }
function GAFeelsLikeSlide({ cities })   { return <BannerMapSlide cities={cities} valueKey="feels_like" />; }
function GADewpointsSlide({ cities })   { return <BannerMapSlide cities={cities} valueKey="dewpoint" />; }
function WindGustsSlide({ cities }) {
  const points = cities
    .map((c) => ({ id: c.id, name: c.name, value: c.wind_gust_mph, dir: c.wind_dir, ...(CITY_COORDS[c.id] || {}) }))
    .filter((p) => p.lat != null);
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="tv-map-slide-wind">
      <TvWindMap points={points} />
    </div>
  );
}

export { FeatureCitySlide, ExtremesSlide, BentoSlide, SevenDaySlide, TempOutlookSlide, BannerMapSlide, GATempsSlide, GAFeelsLikeSlide, GADewpointsSlide, WindGustsSlide };
